/** Theme choice with its cross-device sync: applies through ThemeProvider and mirrors the value into users.preferences so another device picks it up. */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useTheme } from '../../app/ThemeProvider';
import { NativeBridge } from '../../services/NativeBridge';
import { getSupabaseClient } from '../../utils/supabase/client';
import { usePreferences } from './PreferencesContext';
import { getPreferencesSnapshot, subscribePreferences } from './preferencesStore';

type ThemeChoice = 'light' | 'dark' | 'system';

/** Merges the theme into `users.preferences`, preserving its other keys
 *  (email_digest, workingSchedule). Skipped in the macOS shell: a stored 'dark'
 *  there must never fight the native window's system-appearance follow. */
async function syncThemeWithSupabase(userId: string, next: ThemeChoice): Promise<void> {
  if (NativeBridge.isMacShell()) return;

  const client = getSupabaseClient();
  const { data } = await client.from('users').select('preferences').eq('id', userId).single();
  const prefs = ((data as { preferences?: Record<string, unknown> } | null)?.preferences) ?? {};
  await client.from('users').update({ preferences: { ...prefs, theme: next } }).eq('id', userId);
}

/** Applies the theme held in the preferences store to this device once the server row has been read. The store is
 *  canonical; ThemeProvider only mirrors it. chooseTheme changes both together, so the two agree and nothing loops. */
export function useThemeSync(synced: boolean): void {
  const { theme, setTheme } = useTheme();
  const stored = useSyncExternalStore(subscribePreferences, getPreferencesSnapshot, getPreferencesSnapshot).appearance.theme;
  // Native owns light/dark on macOS (ThemeProvider returns early there), so a stored theme cannot be applied.
  const macShell = NativeBridge.isMacShell();

  useEffect(() => {
    if (macShell || !synced || stored === theme) return;
    setTheme(stored);
  // Only a change to the stored value (another device, or a local choice) should apply; theme itself is the thing being set.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored, synced, macShell]);
}

export function useThemePreference(userId: string) {
  const { theme, setTheme } = useTheme();
  const { update } = usePreferences();
  const macShell = NativeBridge.isMacShell();

  const chooseTheme = useCallback((next: ThemeChoice) => {
    setTheme(next);
    // Skipped on macOS: native owns the window appearance there.
    if (!macShell) update({ appearance: { theme: next } });
    // Mirror kept only for older clients that still read users.preferences.theme; the preferences store is canonical.
    syncThemeWithSupabase(userId, next).catch(() => {});
  }, [setTheme, update, userId, macShell]);

  return { theme, chooseTheme, macShell };
}
